import React, { useState, useRef, useEffect } from "react";
import ChevronDownSm from "@/assets/icons/ChevronDownSm.svg";
import { useWorkspaceStore } from "@/hooks/useWorkspace";
import ToolbarLogo from "@/assets/icons/ToolbarLogo.svg";
import WorkspacesDropdown from "./WorkspacesDropdown";
import useUserData from "@/hooks/useUserData";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { AnimatePresence } from "motion/react";
import CreateWorkspaceModal from "./CreateWorkspaceModal";
import InviteUsersModal from "./InviteUsersModal";
import RecordingLinkModal from "./RecordingLinkModal";
import { useNavigate } from "react-router-dom";
import { Workspace } from "@/lib/types";
import InviteCircles from "@/assets/icons/InviteCircles.svg";
import PlusWhite from "@/assets/icons/PlusWhite.svg";

const WorkspacesHeader = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentWorkspace, setCurrentWorkspace } = useWorkspaceStore();
  const { user } = useUserData();
  const workspacesRef = useRef(null);
  const [isOpenCreateWorkspaceModal, setIsOpenCreateWorkspaceModal] =
    useState(false);
  const [isOpenInviteModal, setIsOpenInviteModal] = useState(false);
  const [isOpenRecordingLinkModal, setIsOpenRecordingLinkModal] =
    useState(false);
  const [showInstallExtension, setShowInstallExtension] = useState(false);
  const navigate = useNavigate();

  const showUI = !location.pathname.includes("/feedback");

  useOutsideClick(workspacesRef, () => {
    setIsOpen(false);
  });

  const isUserInWorkspace = currentWorkspace?.users
    .map((user) => user._id)
    .includes(user?._id);

  const checkExtension = () => {
    const extensionId = "jjikojjfkjcbjklhldnjdnelfmjjmifg";
    // Check if chrome API exists
    if (
      typeof (window as any).chrome !== "undefined" &&
      (window as any).chrome.runtime
    ) {
      (window as any).chrome.runtime.sendMessage(
        extensionId,
        { type: "ping" },
        (response: any) => {
          if ((window as any).chrome.runtime.lastError) {
            setShowInstallExtension(true);
            // showCustomToast("Chrome extension not installed");
          } else {
            // console.log("Extension is installed:", response);
            setShowInstallExtension(false);
            // showCustomToast("Extension detected!", "check");
            // Add your create report logic here
          }
        },
      );
    } else {
      setShowInstallExtension(true);
      // console.log("Not running in Chrome or extension API not available");
      // showCustomToast("Please use Chrome browser");
    }
  };

  useEffect(() => {
    checkExtension();
  }, []);

  return (
    <>
      <div className="flex fixed z-[1000] w-full justify-between items-center p-4 pb-3 gap-[10px]">
        <div className="flex items-center gap-4 leading-[17.85px] onest-md min-w-0 w-full">
          <div
            onClick={() => {
              if (user) {
                if (
                  currentWorkspace &&
                  user.workspaces
                    .map((workspace: Workspace) => workspace._id)
                    .includes(currentWorkspace._id)
                ) {
                  navigate(`/dashboard/${currentWorkspace._id}`);
                } else {
                  setCurrentWorkspace(user.workspaces[0]);
                  navigate(`/dashboard/${user.workspaces[0]._id}`);
                }
              } else {
                navigate("/auth");
              }
            }}
            className="shrink-0 cursor-pointer"
          >
            <img src={ToolbarLogo} />
          </div>
          {currentWorkspace && showUI && (
            <p className="shrink-0 text-[14px] text-white/[0.25] cursor-default">
              /
            </p>
          )}
          {currentWorkspace && showUI && (
            <div
              ref={workspacesRef}
              onClick={() => {
                if (isUserInWorkspace) {
                  setIsOpen((prev) => !prev);
                }
              }}
              className={`relative z-[1000] flex gap-1.5 items-center ${
                isUserInWorkspace ? "cursor-pointer" : ""
              }`}
            >
              <div className="flex gap-3 items-center">
                <div className="relative text-[12px] leading-[16px] font-semibold">
                  <div
                    style={{ color: currentWorkspace?.color || "#FF0088" }}
                    className="absolute uppercase top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-white z-[10]"
                  >
                    {currentWorkspace?.name[0]}
                  </div>
                  <div
                    style={{ background: currentWorkspace?.color || "#FF0088" }}
                    className="opacity-[0.16] uppercase w-6 h-6 flex items-center text-[12px] leading-[16px] font-semibold text-gray-900 justify-center rounded-[8px]"
                  />
                </div>

                <div className="leading-[24px] text-[16px] font-medium">
                  {currentWorkspace?.name}
                </div>
              </div>
              {isUserInWorkspace && (
                <img
                  className={`${
                    isOpen ? "rotate-[-180deg]" : ""
                  } transition-all`}
                  src={ChevronDownSm}
                />
              )}
              <AnimatePresence>
                {isOpen && (
                  <WorkspacesDropdown
                    setIsOpenModal={setIsOpenCreateWorkspaceModal}
                  />
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
        <div className="shrink-0 flex gap-[8px]">
          {isUserInWorkspace && showUI && (
            <>
              <button
                onClick={() => setIsOpenInviteModal((prev) => !prev)}
                className="hidden min-[600px]:flex pl-[10px] pr-[12px] gap-1.5 items-center justify-center font-medium h-[36px] text-[14px] leading-[20px] bg-gray-200 hover:bg-gray-300 transition-all rounded-[12px]"
              >
                <img src={InviteCircles} />
                Invite
              </button>
              <button
                onClick={() => setIsOpenRecordingLinkModal((prev) => !prev)}
                className="hidden min-[600px]:flex px-[12px] gap-1.5 items-center justify-center font-medium h-[36px] text-[14px] leading-[20px] bg-blue hover:bg-blue/[0.8] transition-all rounded-[12px]"
              >
                Request a recording
                <img src={PlusWhite} className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isOpenCreateWorkspaceModal && (
          <CreateWorkspaceModal setIsOpen={setIsOpenCreateWorkspaceModal} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpenInviteModal && (
          <InviteUsersModal setIsOpen={setIsOpenInviteModal} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpenRecordingLinkModal && (
          <RecordingLinkModal setIsOpen={setIsOpenRecordingLinkModal} />
        )}
      </AnimatePresence>
    </>
  );
};

export default WorkspacesHeader;
